import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getDrone, patchDrone, commTestDrone, api } from '../api'
import CameraStreamPlayer from '../components/CameraStreamPlayer'
import DroneControlMap, { DroneCommand } from '../components/DroneControlMap'
import './DroneDetail.css'

type MissionItem = {
  id: string
  name: string
  type: string
  action_type?: string
  status: string
  created_at?: string
  waypoints?: unknown[]
}

const ACTION_LABEL: Record<string, string> = {
  land: '🛬 נחיתה', takeoff: '🛫 המראה',
  drop_delivery: '📦 הורדת משלוח', scan: '🔍 סריקה', spray: '💧 ריסוס',
}
const STATUS_COLOR: Record<string, string> = {
  draft: '#93c5fd', active: '#4ade80', completed: '#4ade80', cancelled: '#888',
}

// ─── Comm-test result type ────────────────────────────────────────────────────
type CommResult = {
  status: 'healthy' | 'degraded' | 'unreachable'
  ping: boolean
  latency_ms: number | null
  signal_strength: number
  sim_configured: boolean
  sim_registered: boolean
  sms_capable: boolean
  sms_sent: boolean
  sms_ack: boolean
  phone_number: string | null
  last_heartbeat: string | null
  checked_at: number
}

const SIGNAL_BARS = (strength: number) => {
  const bars = Math.round(strength / 25)
  return ['▁','▂','▃','▄'].map((b, i) => (
    <span key={i} style={{ opacity: i < bars ? 1 : 0.2, color: strength > 60 ? '#4ade80' : strength > 30 ? '#fbbf24' : '#f87171' }}>
      {b}
    </span>
  ))
}

export default function DroneDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [streamUrl, setStreamUrl]       = useState('')
  const [notes, setNotes]               = useState('')
  const [phoneNumber, setPhoneNumber]   = useState('')
  const [controlMode, setControlMode]   = useState(false)
  const [cmdHistory, setCmdHistory]     = useState<{ cmd: DroneCommand; lat: number; lng: number; ts: number }[]>([])
  const [commResult, setCommResult]     = useState<CommResult | null>(null)
  const [commTesting, setCommTesting]   = useState(false)
  const [commError, setCommError]       = useState<string | null>(null)

  const { data: drone, isLoading, error } = useQuery({
    queryKey: ['drone', id],
    queryFn: () => getDrone(id!),
    enabled: !!id,
    refetchInterval: 10000,
  })

  useEffect(() => {
    if (drone) {
      setStreamUrl(drone.camera_stream_url || '')
      setNotes(drone.notes || '')
      setPhoneNumber(drone.phone_number || '')
    }
  }, [drone])

  const { data: missions = [] } = useQuery<MissionItem[]>({
    queryKey: ['missions', id],
    queryFn: () => api.get(`/missions?drone_id=${id}`).then(r => r.data),
    enabled: !!id,
  })

  const saveMutation = useMutation({
    mutationFn: () =>
      patchDrone(id!, {
        camera_stream_url: streamUrl.trim(),
        notes: notes.trim(),
        phone_number: phoneNumber.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drone', id] })
      queryClient.invalidateQueries({ queryKey: ['drones'] })
    },
  })

  const handleCommand = (cmd: DroneCommand, lat: number, lng: number) => {
    setCmdHistory(prev => [{ cmd, lat, lng, ts: Date.now() }, ...prev].slice(0, 10))
    // In production: send command to drone controller via WebSocket/MQTT
    console.info(`[DroneCommand] ${cmd} → drone:${id} @ ${lat.toFixed(5)},${lng.toFixed(5)}`)
  }

  const runCommTest = async () => {
    if (!id) return
    setCommTesting(true)
    setCommError(null)
    setCommResult(null)
    try {
      const result = await commTestDrone(id)
      setCommResult(result)
    } catch (e: unknown) {
      setCommError(e instanceof Error ? e.message : 'שגיאת תקשורת')
    } finally {
      setCommTesting(false)
    }
  }

  if (!id) return <p className="page-error">מזהה רחפן חסר</p>
  if (isLoading) return <div className="page-loading">{t('common.loading')}</div>
  if (error || !drone) {
    return (
      <div className="page-error">
        לא נמצא רחפן או שגיאת רשת.
        <Link to="/drones"> חזרה לרשימה</Link>
      </div>
    )
  }

  const gpsLabel = drone.gps_fix_type != null
    ? ['NoGPS','NoFix','2D Fix','3D Fix','DGPS','RTK'][drone.gps_fix_type] ?? '—'
    : '—'

  return (
    <div className="drone-detail-page">
      {/* ─── Header ─── */}
      <div className="drone-detail-header">
        <Link to="/drones" className="drone-detail-back">← {t('droneDetail.back')}</Link>
        <div className="drone-detail-title-row">
          <h1>{t('droneDetail.title')}: <strong>{drone.drone_id}</strong></h1>
          <div className="drd-header-btns">
            <button
              className={`btn-take-control ${controlMode ? 'active' : ''}`}
              onClick={() => setControlMode(v => !v)}
            >
              {controlMode ? '🔴 עצור שליטה' : '🕹️ קח שליטה'}
            </button>
            <button
              className="btn-create-mission"
              onClick={() => navigate(`/missions/new?drone_id=${id}`)}
            >
              🗺️ צור משימה
            </button>
          </div>
        </div>
        <div className="drone-detail-meta">
          <span className={`status status-${drone.status}`}>{drone.status}</span>
          {drone.drone_model && <span>{drone.drone_model}</span>}
          {drone.tail_number && <span>זנב: {drone.tail_number}</span>}
        </div>
      </div>

      {/* ─── Operational Monitoring Strip ─── */}
      <div className="drone-monitor-strip">
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">סטטוס</span>
          <span className={`drone-monitor-value drone-mon-status status-${drone.status}`}>
            {drone.status === 'online'      ? '🟢 מחובר'
             : drone.status === 'flying'    ? '✈️ טס'
             : drone.status === 'maintenance'? '🔧 תחזוקה'
             : '⚫ לא מחובר'}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">סוללה</span>
          <span className={`drone-monitor-value drone-mon-battery ${
            drone.battery_level == null ? '' :
            drone.battery_level < 20    ? 'battery-critical' :
            drone.battery_level < 50    ? 'battery-low' : 'battery-ok'
          }`}>
            {drone.battery_level != null ? `${drone.battery_level}%` : <span className="drone-mon-na">—</span>}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">GPS</span>
          <span className="drone-monitor-value">
            {gpsLabel !== '—' ? gpsLabel : <span className="drone-mon-na">—</span>}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">מצב טיסה</span>
          <span className="drone-monitor-value drone-mon-mode">
            {drone.flight_mode ?? <span className="drone-mon-na">—</span>}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">גובה</span>
          <span className="drone-monitor-value">
            {drone.altitude != null ? `${drone.altitude}m` : <span className="drone-mon-na">—</span>}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">מהירות</span>
          <span className="drone-monitor-value">
            {drone.speed != null ? `${drone.speed}m/s` : <span className="drone-mon-na">—</span>}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">פעימה אחרונה</span>
          <span className="drone-monitor-value drone-mon-heartbeat">
            {drone.last_heartbeat_at
              ? new Date(drone.last_heartbeat_at).toLocaleTimeString('he-IL')
              : <span className="drone-mon-na">—</span>}
          </span>
        </div>
        <div className="drone-monitor-sep" />
        <div className="drone-monitor-item">
          <span className="drone-monitor-label">משימות</span>
          <span className="drone-monitor-value drone-mon-missions">{missions.length}</span>
        </div>
      </div>

      {/* ─── Operational Map (full drone control surface) ─── */}
      <section className="drone-op-map panel">
        <div className="drone-op-map-header">
          <h2>
            {controlMode ? '🕹️ שליטה ישירה — ArduPilot' : '📍 מפה תפעולית'}
          </h2>
          {drone.last_lat && drone.last_lng ? (
            <span className="drone-op-gps-badge">
              <code dir="ltr">
                {parseFloat(drone.last_lat).toFixed(5)}, {parseFloat(drone.last_lng).toFixed(5)}
              </code>
            </span>
          ) : (
            <span className="drone-op-gps-na">GPS לא זמין</span>
          )}
        </div>

        <DroneControlMap
          dronePosition={
            drone.last_lat && drone.last_lng
              ? { lat: parseFloat(drone.last_lat), lng: parseFloat(drone.last_lng) }
              : undefined
          }
          controlMode={controlMode}
          readOnly={false}
          onCommandIssued={handleCommand}
        />

        {/* Command history log */}
        {cmdHistory.length > 0 && (
          <div className="drd-cmd-history">
            <div className="drd-cmd-history-title">📋 יומן פקודות</div>
            {cmdHistory.map((entry, i) => (
              <div key={i} className="drd-cmd-row">
                <span className="drd-cmd-label">{entry.cmd.replace('_', ' ').toUpperCase()}</span>
                <span className="drd-cmd-coords" dir="ltr">
                  {entry.lat.toFixed(4)}, {entry.lng.toFixed(4)}
                </span>
                <span className="drd-cmd-time">
                  {new Date(entry.ts).toLocaleTimeString('he-IL')}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ─── Camera ─── */}
      <section className="drone-detail-camera">
        <h2>{t('droneDetail.camera')}</h2>
        <CameraStreamPlayer url={streamUrl.trim() || drone.camera_stream_url} />
      </section>

      {/* ─── Settings + Communication ─── */}
      <section className="drone-detail-form panel">
        <h3>{t('droneDetail.streamSettings')}</h3>
        <p className="drone-detail-help">{t('droneDetail.streamHelp')}</p>

        <label>
          {t('droneDetail.streamUrl')}
          <input
            type="url"
            value={streamUrl}
            onChange={e => setStreamUrl(e.target.value)}
            placeholder="https://..."
            dir="ltr"
            className="drone-stream-input"
          />
        </label>

        <label>
          {t('common.notes') || 'הערות'}
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder="הערות..."
          />
        </label>

        {/* ─── SIM / Phone section ─── */}
        <div className="drd-sim-section">
          <h4 className="drd-sim-title">📡 תקשורת SIM</h4>
          <label>
            מספר טלפון רחפן (כרטיס SIM)
            <input
              type="tel"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              placeholder="+972-5X-XXX-XXXX"
              dir="ltr"
              className="drone-stream-input"
            />
          </label>
          {drone.sim_number && (
            <p className="drd-sim-meta">מספר SIM: <code dir="ltr">{drone.sim_number}</code></p>
          )}
          {drone.cellular_provider && (
            <p className="drd-sim-meta">ספק: {drone.cellular_provider}</p>
          )}

          {/* Communication health check */}
          <div className="drd-comm-check">
            <button
              type="button"
              className="btn-comm-test"
              onClick={runCommTest}
              disabled={commTesting}
            >
              {commTesting ? '⏳ בודק...' : '📶 בדיקת תקשורת'}
            </button>

            {commError && <p className="drd-comm-error">❌ {commError}</p>}

            {commResult && (
              <div className={`drd-comm-result status-comm-${commResult.status}`}>
                <div className="drd-comm-row">
                  <span className="drd-comm-key">סטטוס</span>
                  <span className={`drd-comm-val comm-status-badge ${commResult.status}`}>
                    {commResult.status === 'healthy'     ? '✅ תקין'
                     : commResult.status === 'degraded'  ? '⚠️ חלקי'
                     : '❌ לא מגיע'}
                  </span>
                </div>
                <div className="drd-comm-row">
                  <span className="drd-comm-key">Ping</span>
                  <span className="drd-comm-val">
                    {commResult.ping
                      ? `✅ ${commResult.latency_ms} ms`
                      : '❌ timeout'}
                  </span>
                </div>
                <div className="drd-comm-row">
                  <span className="drd-comm-key">עוצמת אות</span>
                  <span className="drd-comm-val drd-signal">
                    {SIGNAL_BARS(commResult.signal_strength)} {commResult.signal_strength}%
                  </span>
                </div>
                <div className="drd-comm-row">
                  <span className="drd-comm-key">SIM רשום</span>
                  <span className="drd-comm-val">{commResult.sim_registered ? '✅' : '❌'}</span>
                </div>
                <div className="drd-comm-row">
                  <span className="drd-comm-key">SMS</span>
                  <span className="drd-comm-val">
                    {commResult.sms_capable
                      ? commResult.sms_sent
                        ? commResult.sms_ack ? '✅ נשלח + אושר' : '⚠️ נשלח, אין אישור'
                        : '⚠️ לא נשלח'
                      : '❌ לא מוגדר טלפון'}
                  </span>
                </div>
                {commResult.last_heartbeat && (
                  <div className="drd-comm-row">
                    <span className="drd-comm-key">פעימה אחרונה</span>
                    <span className="drd-comm-val">
                      {new Date(commResult.last_heartbeat).toLocaleTimeString('he-IL')}
                    </span>
                  </div>
                )}
                <div className="drd-comm-footer">
                  נבדק ב: {new Date(commResult.checked_at * 1000).toLocaleTimeString('he-IL')}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-primary"
            disabled={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? t('common.loading') : t('common.save')}
          </button>
        </div>
        {saveMutation.isError && (
          <p className="form-error">{(saveMutation.error as Error).message}</p>
        )}
      </section>

      {/* ─── Missions ─── */}
      <section className="drone-detail-missions panel">
        <div className="drone-missions-header">
          <h2>משימות מקושרות</h2>
          <button
            className="btn-create-mission"
            onClick={() => navigate(`/missions/new?drone_id=${id}`)}
          >
            + צור משימה
          </button>
        </div>
        {missions.length === 0 ? (
          <p className="drone-missions-empty">אין משימות לרחפן זה עדיין.</p>
        ) : (
          <div className="drone-missions-list">
            {missions.map(m => (
              <Link key={m.id} to={`/missions/${m.id}`} className="drone-mission-card">
                <div className="drone-mission-name">{m.name}</div>
                <div className="drone-mission-meta">
                  {m.action_type && (
                    <span className="drone-mission-action">{ACTION_LABEL[m.action_type] ?? m.action_type}</span>
                  )}
                  <span className="drone-mission-status" style={{ color: STATUS_COLOR[m.status] ?? '#ccc' }}>
                    {m.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
